import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import TestQuestion from '../components/test/TestQuestion';
import TestLayout from '../components/test/TestLayout';
import TestControls from '../components/test/TestControls';
import ConfirmAbandonModal from '../components/test/ConfirmAbandonModal';
import FreeTrialLimitModal from '../components/auth/FreeTrialLimitModal';
import useTestLogic from '../hooks/useTestLogic';
import { TEST_CONFIG } from '../utils/testConstants';
import freeTrialManager from '../services/auth/freeTrialManager';
import TrialService from '../services/auth/TrialService';
import { useAuth } from '../context/AuthContext';

/**
 * TestAleatorio component - Random test mode with immediate progression
 */
const TestAleatorio = () => {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type') || 'viajeros';
  const { isAuthenticated } = useAuth();
  const [showTrialLimitModal, setShowTrialLimitModal] = useState(false);
  const [trialStatus, setTrialStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [canTakeTest, setCanTakeTest] = useState(true);
  
  // Check trial status when component mounts
  useEffect(() => {
    const checkTrialStatus = async () => {
      // Only check for non-authenticated users (free trial)
      if (!isAuthenticated) {
        setIsLoading(true);
        // Get the current trial status
        const status = await TrialService.getUserStatus(true);
        setTrialStatus(status);
        
        // Determine if user can take the test
        const hasAvailableTests = status.testsAvailable > 0;
        setCanTakeTest(isAuthenticated || hasAvailableTests);
        
        // Show the trial limit modal with countdown if no tests are available
        if (!hasAvailableTests) {
          setShowTrialLimitModal(true);
        }
        
        setIsLoading(false);
      } else {
        // Authenticated users can always take the test
        setCanTakeTest(true);
        setIsLoading(false);
      }
    };

    checkTrialStatus();
  }, [isAuthenticated]);
  
  // Use the shared test logic hook with random mode settings only if user can take the test
  const testLogic = useTestLogic({
    type,
    isPracticeMode: false,
    questionCount: TEST_CONFIG.TOTAL_QUESTIONS,
    enabled: canTakeTest && !isLoading
  });
  
  const {
    preguntas,
    preguntaActual,
    respuestas,
    testCompletado,
    cargando,
    error,
    testStatus,
    showConfirmModal,
    resultado,
    preguntaActualObj,
    respuestaActual,
    isLastQuestion,
    setShowConfirmModal,
    handleRespuesta,
    handleSaltar,
    handleFinalizarTest,
    handleConfirmAbandon
  } = testLogic;

  // Record that a test was taken only when the test is completed
  useEffect(() => {
    const recordTestUsage = async () => {
      // Only record test usage for non-authenticated users (free trial) when test is completed
      if (!isAuthenticated && testCompletado) {
        await freeTrialManager.recordTestTaken();
        console.log('Test completado y registrado como usado');
        
        // Force refresh the trial status to ensure it's updated
        const status = await TrialService.getUserStatus(true);
        setTrialStatus(status);
        setCanTakeTest(false);
        
        // Show the trial limit modal with countdown
        setShowTrialLimitModal(true);
      }
    };

    recordTestUsage();
  }, [isAuthenticated, testCompletado]);

  // Common letters for answer options
  const letras = ['A', 'B', 'C', 'D'];

  // Check if there are no questions to display
  const noQuestionsFound = !preguntas.length || !preguntaActualObj;

  // If user can't take the test, render only the modal
  if (!canTakeTest && !isLoading) {
    return (
      <div style={{ backgroundColor: 'white', minHeight: '100vh' }}>
        <FreeTrialLimitModal
          isOpen={showTrialLimitModal}
          onClose={() => setShowTrialLimitModal(false)}
          initialStatus={trialStatus}
        />
      </div>
    );
  }

  return (
    <>
      <TestLayout
        title={`TEST REAL ${type === 'viajeros' ? 'VIAJEROS' : 'MERCANCÍAS'}`}
        type={type}
        testStatus={testStatus}
        cargando={cargando || isLoading}
        error={error}
        testCompletado={testCompletado}
        resultado={resultado}
        noQuestionsFound={noQuestionsFound}
        preguntaActual={preguntaActual}
        totalQuestions={TEST_CONFIG.TOTAL_QUESTIONS}
      >
        {/* Only render if we have questions and user can take the test */}
        {preguntaActualObj && canTakeTest && !isLoading && (
          <>
            <TestQuestion
              pregunta={preguntaActualObj.pregunta}
              opciones={preguntaActualObj.opciones}
              letras={letras}
              respuestaActual={respuestaActual}
              respuestaCorrecta={preguntaActualObj.respuestaCorrecta}
              onRespuesta={handleRespuesta}
              disabled={false}
              currentQuestion={preguntaActual}
              totalQuestions={TEST_CONFIG.TOTAL_QUESTIONS}
              isPracticeMode={false}
            />
            
            <TestControls
              onSaltar={handleSaltar}
              onFinalizar={handleFinalizarTest}
              isLastQuestion={isLastQuestion}
            />
          </>
        )}
      </TestLayout>
      
      {/* Confirmation modal for back navigation */}
      <ConfirmAbandonModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmAbandon}
      />
      
      {/* Free Trial Limit Modal with countdown - only show if user can take test */}
      {canTakeTest && (
        <FreeTrialLimitModal
          isOpen={showTrialLimitModal}
          onClose={() => setShowTrialLimitModal(false)}
          initialStatus={trialStatus}
        />
      )}
    </>
  );
};

const WrappedTestAleatorio = () => (
  <ProtectedRoute>
    <TestAleatorio />
  </ProtectedRoute>
);

export default WrappedTestAleatorio;